/**
 * Browser adapter for the offline demo API.
 *
 * Static hosting (GitHub Pages) has no server to run the dev middleware, so
 * the demo answers its own requests: this replaces `window.fetch` and serves
 * anything demoRouter.ts claims, straight from the bundled fixtures. Requests
 * to anything else — the page's own assets — pass through untouched.
 *
 * Installed from main.tsx via the `virtual:march-demo-api` module, which
 * resolves to an empty stub in every non-demo build, so neither this adapter
 * nor the fixtures reach a production bundle.
 */

import { isDemoApiPath, routeDemoRequest } from './demoRouter';
import { DEMO_HISTORY_WINDOW, DEMO_SESSION_ID } from './fixtures/patient';
import { seedRecordedTranscript } from './fixtures/transcript';
import { installDemoReadOnly, isDemoReadOnly } from './demoReadOnly';

/** The keys SMART session resolution reads (api/smartApi.ts). */
const SMART_SESSION_STORAGE_KEY = 'march_smart_session_id';
/** The trend page sends this as its window and shows its label in the header.
 *  Unseeded it falls back to two years, which silently hides most of a chart
 *  whose capture covers decades. */
const SMART_HISTORY_WINDOW_STORAGE_KEY = 'march_smart_history_window';

/**
 * Open the demo patient by default.
 *
 * Without a SMART session the app has no patient, so `/` resolves to
 * "Open a patient to begin" — correct for the real product, where a chart is
 * opened from the EHR, and a dead end for a demo link. Seeding the session id
 * puts the demo into the app's own same-tab resume path: the workspace opens
 * on the demo patient, exactly as it would after a launch.
 *
 * Deliberately does not override anything: a real `?session_id=` on the URL
 * takes precedence in resolution and still gets the full launch flow (window
 * picker, staged import), and an existing session is left alone.
 */
function openDemoPatientByDefault(): void {
  try {
    if (new URL(window.location.href).searchParams.get('session_id')) return;
    const store = window.sessionStorage;
    if (store.getItem(SMART_SESSION_STORAGE_KEY)) return;
    store.setItem(SMART_SESSION_STORAGE_KEY, DEMO_SESSION_ID);
    store.setItem(SMART_HISTORY_WINDOW_STORAGE_KEY, DEMO_HISTORY_WINDOW);
  } catch {
    // Private-mode storage failures just mean the launch screen shows.
  }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Turn the router's SSE generator into a streaming Response body. */
function sseResponse(open: () => AsyncGenerator<string, void, unknown>): Response {
  const encoder = new TextEncoder();
  const frames = open();
  const stream = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const next = await frames.next();
        // `done` and a void value both mean the stream is finished; checking
        // the value too keeps the narrowing honest for AsyncGenerator<_, void>.
        if (next.done === true || next.value === undefined) controller.close();
        else controller.enqueue(encoder.encode(next.value));
      } catch {
        controller.close();
      }
    },
    // The consumer went away (navigation, an aborted request) — let the
    // generator unwind instead of leaving its timers pending.
    cancel() {
      void frames.return(undefined);
    },
  });
  return new Response(stream, {
    status: 200,
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache, no-transform',
    },
  });
}

/** The request's method, URL and body, however fetch() was called. */
function describe(input: RequestInfo | URL, init?: RequestInit) {
  const request = input instanceof Request ? input : null;
  const href = request ? request.url : String(input instanceof URL ? input.href : input);
  const url = new URL(href, window.location.href);
  const method = (init?.method ?? request?.method ?? 'GET').toUpperCase();
  const readBody = async (): Promise<Record<string, unknown>> => {
    try {
      const raw = init?.body != null ? String(init.body) : request ? await request.clone().text() : '';
      if (!raw.trim()) return {};
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  };
  return { url, method, readBody };
}

let installed = false;

export function installDemoApi(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  openDemoPatientByDefault();
  if (isDemoReadOnly()) {
    // The composer is disabled, so the conversation has to already be there.
    seedRecordedTranscript();
    installDemoReadOnly();
  }

  const passthrough = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const { url, method, readBody } = describe(input, init);
    if (!isDemoApiPath(url.pathname)) return passthrough(input as RequestInfo, init);

    const result = await routeDemoRequest(method, url, readBody);

    if (result.kind === 'passthrough') return passthrough(input as RequestInfo, init);
    if (result.kind === 'sse') return sseResponse(result.open);
    if (result.kind === 'empty') {
      return new Response(null, { status: result.status });
    }

    // Keep the simulated latency: it is what makes the demo's loading states
    // visible, and several of them are the point of the screen.
    await sleep(result.delayMs);
    // An aborted request must reject like a real one rather than resolving
    // late — the app cancels in-flight chart polls on navigation.
    if (init?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return new Response(JSON.stringify(result.body), {
      status: result.status,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  // The telemetry path is fire-and-forget over sendBeacon, which bypasses
  // fetch entirely. Swallow it so a static host never sees a stray 404.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const beacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (beaconUrl: string | URL, data?: BodyInit | null): boolean => {
      try {
        const resolved = new URL(String(beaconUrl), window.location.href);
        if (isDemoApiPath(resolved.pathname)) return true;
      } catch {
        /* fall through to the real beacon */
      }
      return beacon(beaconUrl, data);
    };
  }

  // eslint-disable-next-line no-console
  console.info('[march] Offline demo mode — every API call is served from bundled fixtures.');
}
